import { ApplicationContext } from '../App';
import {
	Application,
	ApplicationTopic,
	PalletizerState,
	PalletizerInformation,
	PalletSensor,
	default_op_state,
	Alert,
	GenericStateItem_t,
	PalletizerIO_t,
	SafetyStatus,
	ARStatus,
	StackQueue,
} from 'libapi';
import { createContext, useReducer, useEffect, useContext, useState } from 'react';

enum ReductionType {
	Info,
	InfoAppend,
	State,
	RobotState,
	PalletizerMode,
	Alert,
	Queue /* Stack Queue */,
	OperationalState,
	SystemState,
	PalletizerIOs,
	PendingSKUScan,
	InvalidSKUScan,
	BoxErectorPower,
	BoxErectorSystemState /* for box erector IOs*/,
	BoxErectorOperationalState /* for box erector run state and box count */,
	Estop,
	Safety,
	AccessRequest,
	GripperSelected,
	LeftBayStatus,
	RightBayStatus,
}

type ReducerAction = {
	type_of: ReductionType;
	payload: any;
};

export type BayStatus = {
	palletId: number;
	stackId: number;
	position: string;
};

class AlertQueue {
	alerts: Alert[];
	constructor() {
		this.alerts = [];
	}
	push(pAlert: Alert) {
		const lUuid = pAlert.uuid;
		for (let i = 0; i < this.alerts.length; i++)
			if (this.alerts[i].uuid === lUuid) return;
		while (this.alerts.length > 10) this.alerts.shift(); // limit length.
		this.alerts.push(pAlert);
	}
	peek() {
		if (this.alerts.length > 0) return this.alerts[0];
		return void 0;
	}
	shift() {
		this.alerts.shift();
		console.log(this.alerts);
	}
}

class Notifications {
	notifications: PalletizerInformation[];
	new_notification: boolean;
	constructor() {
		this.notifications = [];
		this.new_notification = false;
	}
	push(pInfo: PalletizerInformation) {
		for (let i = 0; i < this.notifications.length; i++) {
			if (
				this.notifications[i].TimeStamp.delta_start === pInfo.TimeStamp.delta_start
			) {
				return;
			}
			while (this.notifications.length > 10) this.notifications.shift();
		}
		this.notifications.unshift(pInfo);
		this.new_notification = true;
	}
	peek() {
		if (this.notifications.length > 0) return this.notifications[0];
		return void 0;
	}
	clear_new_notification() {
		console.log('New notification cleared');
		this.new_notification = false;
	}
	shift() {
		this.notifications.shift();
	}
}

export interface RuntimeContext extends PalletizerState {
	/* alertQueue: AlertQueue; */
	alertQueue: AlertQueue;
	notifications: Notifications;
	StackQueueTimestamp: number;
	genericState: any;
	systemState: GenericStateItem_t;
	palletizerIOs: PalletizerIO_t;
	pendingSKUScan: { [key: string]: string };
	invalidSKUScan: { [key: string]: string };
	eStop: boolean;
	palletizer_mode: boolean /* true = manual mode */;
	safety: { [key: string]: SafetyStatus };
	ar_status: ARStatus;
	selected_gripper: { [key: string]: string };
	leftBayStatus: BayStatus | null;
	rightBayStatus: BayStatus | null;
	version: string;
}

function PalletizerReducer(state: RuntimeContext, action: ReducerAction) {
	switch (action.type_of) {
		case ReductionType.Info:
			const information = action.payload as PalletizerInformation[];
			return {
				...state,
				information,
			} as RuntimeContext;
		case ReductionType.InfoAppend:
			if (!!action.payload)
				state.notifications.push(action.payload as PalletizerInformation);
			return { ...state };
		case ReductionType.State:
			return {
				...state,
				...action.payload,
			} as RuntimeContext;
		case ReductionType.RobotState:
			const op = { ...state.operational_state };
			op.status = action.payload;
			return {
				...state,
				operational_state: op,
			} as RuntimeContext;
		case ReductionType.Alert:
			state.alertQueue.push(action.payload as Alert);
			return { ...state };
		case ReductionType.PalletizerMode:
			return { ...state, palletizer_mode: action.payload as boolean };
		case ReductionType.Queue:
			return { ...state, StackQueueTimestamp: action.payload as number };
		case ReductionType.OperationalState:
			return { ...state, genericState: action.payload };
		case ReductionType.SystemState:
			return { ...state, systemState: action.payload as GenericStateItem_t };
		case ReductionType.PalletizerIOs:
			return { ...state, palletizerIOs: action.payload as PalletizerIO_t };
		case ReductionType.PendingSKUScan:
			const lNewPending = { ...state.pendingSKUScan, ...action.payload };
			return {
				...state,
				pendingSKUScan: lNewPending,
			};
		case ReductionType.InvalidSKUScan:
			const lNewInvalid = { ...state.invalidSKUScan, ...action.payload };
			return {
				...state,
				invalidSKUScan: lNewInvalid,
			};
		case ReductionType.Estop:
			return { ...state, eStop: action.payload as boolean };
		case ReductionType.Safety:
			return {
				...state,
				safety: {
					...state.safety,
					...(action.payload as { [key: string]: SafetyStatus }),
				},
			};
		case ReductionType.AccessRequest:
			return { ...state, ar_status: action.payload as ARStatus };
		case ReductionType.GripperSelected:
			return {
				...state,
				selected_gripper: action.payload as { [key: string]: string },
			};
		case ReductionType.LeftBayStatus:
			return { ...state, leftBayStatus: action.payload as BayStatus };
		case ReductionType.RightBayStatus:
			return { ...state, rightBayStatus: action.payload as BayStatus };
		default:
			return state;
	}
}

export const PalletizerContext = createContext<Partial<RuntimeContext>>({});

export default function PalletizerHub(props: any) {
	const initial_state: RuntimeContext = {
		operational_state: default_op_state(),
		information: [] as PalletizerInformation[],
		pallet_sensors: [] as PalletSensor[],
		alertQueue: new AlertQueue(),
		notifications: new Notifications(),
		StackQueueTimestamp: 0,
		genericState: {},
		systemState: { Title: 'System State', Items: {} },
		palletizerIOs: {},
		pendingSKUScan: {},
		invalidSKUScan: {},
		eStop: false,
		palletizer_mode: false,
		safety: {},
		ar_status: ARStatus.LOCKED,
		selected_gripper: { name: '', tcp: '' },
		leftBayStatus: null,
		rightBayStatus: null,
		version: '0.0.0',
	};

	const app = useContext(ApplicationContext) as Application;
	const [state, dispatch] = useReducer(PalletizerReducer, initial_state);
	const [version, setVersion] = useState<string>('0.0.0');

	// const { all_configs: lConfigs } = useContext(ConfigurationsContext) as any;
	// const lPalletConfig = lConfigs as PalletConfigObj;
	interface version {
		version: string;
		name: string;
	}
	useEffect(() => {
		try {
		app.api.Version.get().then((v: version) => {
			setVersion(`Palletizer: V${v.version}`);
		})
	}	catch (error) {
		console.log(error);
		setVersion('Palletizer: Error getting version number');
	}
		const handle_information = (message: any) => {
				dispatch({
					type_of: ReductionType.Info,
					payload: message,
				});
			},
			handle_info_append = (message: any) => {
				dispatch({
					type_of: ReductionType.InfoAppend,
					payload: message,
				});
			},
			handle_state = (message: any) => {
				dispatch({
					type_of: ReductionType.State,
					payload: message,
				});
			},
			handle_robot_state = (message: any) => {
				dispatch({
					type_of: ReductionType.RobotState,
					payload: message,
				});
			},
			handle_alert = (message: any) => {
				dispatch({
					type_of: ReductionType.Alert,
					payload: message,
				});
			},
			lHandlePalletizerMode = function (pMessage: boolean) {
				dispatch({
					type_of: ReductionType.PalletizerMode,
					payload: pMessage,
				});
			},
			lHandleStackQueueTimestamp = function (pMessage: number) {
				dispatch({ type_of: ReductionType.Queue, payload: pMessage });
			},
			lHandleSystemState = function (pMessage: GenericStateItem_t) {
				dispatch({ type_of: ReductionType.SystemState, payload: pMessage });
			},
			lHandlePalletizerIOs = function (pMessage: any) {
				dispatch({ type_of: ReductionType.PalletizerIOs, payload: pMessage });
			},
			lHandlePendingSKUScan = function (pMessage: any) {
				dispatch({ type_of: ReductionType.PendingSKUScan, payload: pMessage });
			},
			lHandleInvalidSKUScan = function (pMessage: any) {
				dispatch({ type_of: ReductionType.InvalidSKUScan, payload: pMessage });
			},
			lHandleOperationalState = function (pMessage: GenericStateItem_t) {
				dispatch({ type_of: ReductionType.OperationalState, payload: pMessage });
			},
			lHandleBoxErectorSystemState = function (pMessage: GenericStateItem_t) {
				dispatch({
					type_of: ReductionType.BoxErectorSystemState,
					payload: pMessage,
				});
			},
			lHandleBoxErectorOperationalState = function (pMessage: GenericStateItem_t) {
				dispatch({
					type_of: ReductionType.BoxErectorOperationalState,
					payload: pMessage,
				});
			},
			lHandleEstop = function (pMessage: boolean) {
				dispatch({ type_of: ReductionType.Estop, payload: pMessage });
			},
			lHandleSafety = function (t: string, m: string) {
				const match = /palletizer\/safety\/([\w\d]+)/im.exec(t);
				if (!match) {
					console.warn('unmatched safety message', t, m);
					return;
				}
				let int_val = +m;
				if (isNaN(int_val)) {
					console.error('invalid safety message', m, int_val, t);
					return;
				}
				const pair = { [match[1]]: int_val as SafetyStatus };
				dispatch({
					type_of: ReductionType.Safety,
					payload: pair,
				});
			},
			lHandleAccessRequest = function (pMessage: ARStatus) {
				dispatch({
					type_of: ReductionType.AccessRequest,
					payload: pMessage,
				});
			},
			lHandleGripperSelected = function (pMessage: { [key: string]: string }) {
				dispatch({
					type_of: ReductionType.GripperSelected,
					payload: pMessage,
				});
			},
			lHandlePalletConfigUpdated = function () {
				app.api.Pallet.get()
					.then(cfg => {
						app.api.Queue.get().then(function (pQueue: StackQueue) {
							const lNewQueue: any = [];
							try {
								for (let i = 0; i < pQueue.length; i++) {
									const lQueueItem = pQueue[i];
									// Get the stack uuid from the stack queue.
									const lStackUUID = lQueueItem.StackUUID;
									const lNewQueueItem = { ...lQueueItem };
									let lFoundMatch = false;
									for (let p = 0; p < cfg.config.pallets.length; p++) {
										if (lFoundMatch) break;
										const lPallet = cfg.config.pallets[p];
										for (let s = 0; s < lPallet.Stack.length; s++) {
											if (lPallet.Stack[s].id === lStackUUID) {
												lNewQueueItem.StackID = s;
												lNewQueueItem.PalletID = p;
												// The rest remains the same.
												lFoundMatch = true;
												break;
											}
										}
									}
									if (lFoundMatch) {
										lNewQueue.push(lNewQueueItem);
									}
								}
								if (lNewQueue.length !== pQueue.length) {
									app.stop();
								}
								app.api.Queue.post(lNewQueue)
									.then(() => {
										localStorage.setItem('reloaded', 'true');
										window.location.reload();
									})
									.catch(e => {
										console.log('ERROR');
										console.error('Error updating queue against new config', e);
										localStorage.setItem('reloaded', 'true');
										window.location.reload();
									});
							} catch {
								console.log('Error: 429');
							}
						});
					})
					.catch(_error => {
						app.api.Queue.post([]).catch(console.error);
						app.clearBPM();
						app.stop();
						console.error('Error updating config during remote deployment.');
						localStorage.setItem('reloaded', 'true');
						window.location.reload();
					});
			},
			lHandlerId = app.registerMQTTHandler((t: string, m: string) => {
				try {
					switch (t) {
						case ApplicationTopic.Information:
							handle_information(JSON.parse(m));
							break;
						case ApplicationTopic.AppendInformation:
							handle_info_append(JSON.parse(m));
							break;
						case ApplicationTopic.State:
							handle_state(JSON.parse(m));
							break;
						case ApplicationTopic.Robot:
							handle_robot_state(JSON.parse(m));
							break;
						case ApplicationTopic.Alert:
							handle_alert(JSON.parse(m));
							break;
						case ApplicationTopic.PalletizerMode:
							lHandlePalletizerMode(JSON.parse(m) as boolean);
							break;
						case ApplicationTopic.Queue:
							lHandleStackQueueTimestamp(JSON.parse(m) as number);
							break;
						case ApplicationTopic.OperationalState:
							lHandleOperationalState(JSON.parse(m) as GenericStateItem_t);
							break;
						case ApplicationTopic.SystemState:
							lHandleSystemState(JSON.parse(m) as GenericStateItem_t);
							break;
						case ApplicationTopic.PalletizerIOs:
							lHandlePalletizerIOs(JSON.parse(m) as any);
							break;
						case ApplicationTopic.PendingSKUScan:
							lHandlePendingSKUScan(JSON.parse(m) as any);
							break;
						case ApplicationTopic.InvalidSKUScan:
							lHandleInvalidSKUScan(JSON.parse(m) as any);
							break;

						case ApplicationTopic.BoxErectorSystemState:
							lHandleBoxErectorSystemState(JSON.parse(m) as GenericStateItem_t);
							break;
						case ApplicationTopic.BoxErectorOperationalState:
							lHandleBoxErectorOperationalState(JSON.parse(m) as GenericStateItem_t);
							break;
						case ApplicationTopic.Estop:
							lHandleEstop(!!JSON.parse(m));
							break;
						case ApplicationTopic.AccessRequest:
							lHandleAccessRequest(JSON.parse(m) as ARStatus);
							break;
						case ApplicationTopic.ConveyorManualStart:
							break;
						case ApplicationTopic.GripperSelected:
							lHandleGripperSelected(JSON.parse(m) as { [key: string]: string });
							break;
						case ApplicationTopic.PalletConfigUpdated:
							console.log('Pallet config updated!!!!');
							lHandlePalletConfigUpdated();
							break;
						case ApplicationTopic.LeftBayStatus:
							const leftStatus = m?.trim() === '' ? null : JSON.parse(m);
							dispatch({ type_of: ReductionType.LeftBayStatus, payload: leftStatus });
							break;
						case ApplicationTopic.RightBayStatus:
							const rightStatus = m?.trim() === '' ? null : JSON.parse(m);
							dispatch({
								type_of: ReductionType.RightBayStatus,
								payload: rightStatus,
							});
							break;
						default:
							if (/palletizer\/safety/.test(t)) {
								lHandleSafety(t, m);
								return;
							}
							console.warn('unhandled MQTT topic', t, m);
							break;
					}
				} catch (pError) {
					console.log(pError);
				}
			});
		return function () {
			app.deregisterMQTTHandler(lHandlerId);
		};
	}, [app]);

	return (
		<PalletizerContext.Provider value={{...state, version}}>
			{props.children}
		</PalletizerContext.Provider>
	);
}
